import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Copy, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface StrategyViewerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectName: string;
  strategyDocument: string | null | undefined;
}

export function StrategyViewerModal({
  open,
  onOpenChange,
  projectName,
  strategyDocument,
}: StrategyViewerModalProps) {
  const { toast } = useToast();

  const handleCopy = () => {
    if (!strategyDocument) return;
    navigator.clipboard.writeText(strategyDocument);
    toast({
      title: "Copied",
      description: "Strategy document copied to clipboard",
    });
  };

  const handleDownload = () => {
    if (!strategyDocument) return;
    const element = document.createElement("a");
    const file = new Blob([strategyDocument], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `${projectName.replace(/\s+/g, "-")}-strategy.md`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <DialogTitle>{projectName} - Strategy Document</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto border rounded-lg bg-muted/20 p-6 min-h-0">
          {strategyDocument ? (
            <div className="space-y-4">
              <div className="prose dark:prose-invert prose-sm max-w-none whitespace-pre-wrap break-words text-sm leading-relaxed">
                {strategyDocument}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No strategy document attached</p>
            </div>
          )}
        </div>

        {strategyDocument && (
          <div className="flex-shrink-0 flex gap-2 justify-end pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              data-testid="button-copy-strategy"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              data-testid="button-download-strategy"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
